import React, { useEffect } from 'react';
import { useIsFocused } from '@react-navigation/native';
import { useQuery } from '@tanstack/react-query';
import { platformSettingsService } from '../../../services/central-api/platform-settings';
import { useAuth } from '../../../context/Auth';
import { View } from 'react-native';
import { translate } from '../../../services/translate';
import { useForm } from 'react-hook-form';
import CustomButton from '../../../components/CustomButton';
import Toast from 'react-native-toast-message';
import { createStyleSheet, useStyles } from 'react-native-unistyles';
import MyEmailInput from '../../../components/MyEmailInput';

interface EmailRecipientForm {
    abuseReportEmailRecipient: string | null;
    thirdPartyInscriptionEmailRecipient: string | null;
    userSystemBlockedEmailRecipient: string | null;
}

export default function EmailRecipients() {
    const { user: authenticatedUser } = useAuth();
    const { styles } = useStyles(styleSheet);

    const isFocused = useIsFocused();
    const queryResult = useQuery({
        queryKey: ['platformSettingsEmailRecipients'],
        queryFn: () => platformSettingsService.getEmailRecipients(),
        enabled: !!authenticatedUser && isFocused,
    });

    const { data, refetch } = queryResult;

    const defaultEmptyValues: EmailRecipientForm = {
        abuseReportEmailRecipient: null,
        thirdPartyInscriptionEmailRecipient: null,
        userSystemBlockedEmailRecipient: null
    };

    const form = useForm<EmailRecipientForm>({
        defaultValues: defaultEmptyValues,
        mode: 'onChange'
    });

    useEffect(() => {
        if (!data) {
            return;
        }
        form.reset(data);
        form.trigger();
    }, [data]);

    async function saveEmailRecipients() {
        try {
            await platformSettingsService.updateEmailRecipients(form.getValues());
            Toast.show({
                type: 'sentinelxSuccess',
                text1: translate('ActionSuccessfully'),
            });
            refetch();
        } catch (err) {
            console.error(err);
            Toast.show({
                type: 'sentinelxError',
                text1: translate('unexpectedError'),
            });
        }
    }

    return (
        <View style={{ flex: 1 }}>
            <View style={{ flex: 1, flexDirection: 'column', gap: 8 }}>
                <MyEmailInput
                    label={translate('abuseReportEmailRecipient')}
                    placeholder={translate('abuseReportEmailRecipient')}
                    form={form}
                    fieldName='abuseReportEmailRecipient'
                    labelStyle={styles.fieldsLabelStyle}
                    allowEmptyValue
                />
                <MyEmailInput
                    label={translate('thirdPartyInscriptionEmailRecipient')}
                    placeholder={translate('thirdPartyInscriptionEmailRecipient')}
                    form={form}
                    fieldName='thirdPartyInscriptionEmailRecipient'
                    labelStyle={styles.fieldsLabelStyle}
                    allowEmptyValue
                />
                <MyEmailInput
                    label={translate('userSystemBlockedEmailRecipient')}
                    placeholder={translate('userSystemBlockedEmailRecipient')}
                    form={form}
                    fieldName='userSystemBlockedEmailRecipient'
                    labelStyle={styles.fieldsLabelStyle}
                    allowEmptyValue
                />
            </View>
            <View style={{ flexDirection: 'row', justifyContent: 'flex-end' }}>
                <CustomButton
                    onPress={saveEmailRecipients}
                    disabled={!form.formState.isValid || form.formState.isLoading}
                    text={translate('save')}
                />
            </View>
        </View>);
}

const styleSheet = createStyleSheet((theme) => ({
    fieldsLabelStyle: {
        color: theme.colors.neutral['500'],
        fontWeight: '600',
        fontSize: 14,
    },
}));
